import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import _ from "lodash";
import { Redirect, Route, Switch, withRouter } from "react-router-dom";
import ReduxToastr from "react-redux-toastr";
import Events from "../../events/containers/events-container";
import PrestoPlayerContainer from "../../presto-player/containers/presto-player-container";
import clientPropType from "../../../prop-types/client";
import brandingPropType from "../../../prop-types/branding";
import filtersPropType from "../../../prop-types/eventFilters";
import { fetchEventTypes } from "../../../state/events/actions";
import { fetchClient } from "../../../state/client/actions";
import { updateFilters } from "../../../state/filters/actions";
import Loading from "../../../commons/loading/components/loading";
import PrivateRoute from "../../../commons/private-route/components/private-route";
import eventType from "../../../prop-types/eventType";
import Network from "../../network/containers/network";
import CreateAccount from "../../create-account/containers/create-account-container";
import Login from "../../login/containers/login-container";
import EditPersonalInfo from "../../edit-personal-info/containers/edit-personal-info-container";
import ViewAccount from "../../view-account/containers/view-account-container";
import RecoverAccountPassword from "../../password/components/recover-password";
import OrderHistory from "../../order-history/containers/order-history-container";
import {
  ACCOUNT,
  ACCOUNT_INFORMATION,
  CREATE_ACCOUNT,
  EDIT_PERSONAL_INFO,
  EVENT,
  EVENTS,
  FORGOT_PASSWORD,
  INFO,
  LOGIN,
  NETWORK,
  ORDER_DETAILS,
  ORDER_HISTORY,
  PLAYER,
  PRODUCT_SUMMARY,
  PRODUCTS,
  PROVIDE_SHORTNAME_MESSAGE,
  PURCHASE,
  SOMETHING_WENT_WRONG_MESSAGE,
  SCHEDULED
} from "../../../constants";
import "react-redux-toastr/lib/css/react-redux-toastr.min.css";
import { getItemFromLS } from "../../../utils/util";
import { getAccountByAccountId } from "../../../state/user/actions";
import EventProducts from "../../event-products/containers/event-products";
import ProductSummary from "../../event-products/components/product-summary";
import OrderConfirmation from "../../event-products/components/product-order-confirmation";
import PurchaseContainerStripe from "../../purchases/containers/purchase-container-stripe";
import OrderDetailsContainer from "../../order-details/containers/order-details-container";
import InfoMessage from "../../info/containers/info-message-container";
import Axios from "../../../utils/axios";
import "../styles/home.scss";

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      shortName: PropTypes.string.isRequired
    })
  }).isRequired,
  location: PropTypes.object.isRequired,
  // Map state to props
  eventTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      sport: PropTypes.number
    })
  ),
  client: clientPropType,
  branding: brandingPropType,
  isFetching: PropTypes.bool.isRequired,
  types: PropTypes.arrayOf(eventType).isRequired,
  filters: filtersPropType.isRequired,
  isLoading: PropTypes.bool.isRequired,
  // Map dispatch to props
  fetchEventTypesAction: PropTypes.func.isRequired,
  updateFiltersAction: PropTypes.func.isRequired,
  getAccountAction: PropTypes.func.isRequired
};

const defaultProps = {
  client: {
    onDemand: false,
    id: undefined
  },
  eventTypes: []
};

class Home extends Component {
  constructor(props) {
    super(props);
    this.state = {
      canGetUser: !!(getItemFromLS("userId") && getItemFromLS("tokenId"))
    };
  }

  componentDidUpdate(prevProps) {
    const {
      client,
      branding,
      location,
      eventTypes,
      filters,
      updateFiltersAction,
      getAccountAction,
      fetchEventTypesAction
    } = this.props;

    const searchParams = new URLSearchParams(location.search.toLowerCase());
    if (
      eventTypes &&
      !_.isEmpty(eventTypes) &&
      searchParams.get("sport") &&
      filters.sport !== +searchParams.get("sport")
    ) {
      filters.sport = +searchParams.get("sport");
      updateFiltersAction(filters);
    }

    if (
      !_.isEmpty(client?.categories) &&
      searchParams.get("category") &&
      filters.category !== +searchParams.get("category")
    ) {
      filters.category = +searchParams.get("category");
      updateFiltersAction(filters);
    }

    // Get start and end date params
    const start = +searchParams.get("start-date");
    const end = +searchParams.get("end-date");
    const tab = searchParams.get("tab");
    if (tab === "scheduled") {
      if (
        start &&
        start !== filters.sStartDate.getTime() &&
        end &&
        end !== filters.sEndDate.getTime()
      ) {
        filters.sStartDate = new Date(start);
        filters.sEndDate = new Date(end);
        updateFiltersAction(filters);
      }
    } else if (
      start &&
      start !== filters.oStartDate.getTime() &&
      end &&
      end !== filters.oEndDate.getTime()
    ) {
      filters.oStartDate = new Date(start);
      filters.oEndDate = new Date(end);
      updateFiltersAction(filters);
    }

    const hasClientBrandingChanged =
      client.branding && prevProps.client.branding !== client.branding; // Legacy
    const hasBrandingSettingsChanged =
      !_.isNull(branding) && !_.isEqual(prevProps.branding, branding);

    if (hasClientBrandingChanged || hasBrandingSettingsChanged) {
      document.documentElement.style.setProperty(
        "--portalPrimaryColor",
        branding.themePrimary // Team Color (Video Player)
      );
      document.documentElement.style.setProperty(
        "--portalPrimaryColorWithOpacity",
        `${branding.themePrimary}80` // Team Color (Video Player)
      );
      document.documentElement.style.setProperty(
        "--portalSecondaryColor",
        branding.themeSecondary // Team Color (Header)
      );
      document.documentElement.style.setProperty(
        "--portalTertiary",
        branding.themeTertiary // Background Color
      );
      document.documentElement.style.setProperty(
        "--primaryTextColor",
        branding.themePrimaryText // Text Color (Video Player)
      );
      document.documentElement.style.setProperty(
        "--secondaryTextColor",
        branding.themeSecondaryText // Text Color (Header)
      );
      if (client.branding?.statsColor) {
        document.documentElement.style.setProperty(
          "--statsColor",
          client.branding.statsColor
        );
      }
      if (client.branding?.font) {
        document.documentElement.style.setProperty(
          "--portalFontFamily",
          `"${client.branding.font}"`
        );
      }
    }
    if (!prevProps.client.id && client.id && client.enabled) {
      fetchEventTypesAction();
      const accountId = getItemFromLS("userId");
      const tkn = getItemFromLS("tokenId");
      if (tkn && accountId) {
        Axios.setAuthToken(tkn);
        getAccountAction(accountId);
      }
    }
  }

  render() {
    const {
      client: { id, features },
      types,
      filters,
      location,
      isFetching,
      isLoading,
      match: {
        params: { shortName }
      }
    } = this.props;
    const { canGetUser } = this.state;
    const hasClient = !(_.isNull(id) || _.isUndefined(id));
    const hasError = !isFetching && !hasClient;
    const hasNetwork = hasClient && features.network;
    return (
      <Loading
        isFetching={isFetching}
        noDataMsg={PROVIDE_SHORTNAME_MESSAGE}
        errorMsg={SOMETHING_WENT_WRONG_MESSAGE}
        hasData={hasClient}
        hasError={hasError}
      >
        <ReduxToastr
          timeOut={0}
          newestOnTop={false}
          preventDuplicates
          position="top-center"
          getState={(state) => state.toastr} // This is the default
          transitionIn="fadeIn"
          transitionOut="fadeOut"
          closeOnToastrClick
        />
        <Switch>
          <Route path={`/:shortName/${PLAYER}/${EVENT}/:eventId/:streamType`}>
            <PrestoPlayerContainer filters={filters} />
          </Route>
          <PrivateRoute
            path={`/:shortName/${EVENTS}/:eventId/${PRODUCTS}/:id/${PURCHASE}`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <PurchaseContainerStripe types={types} />
          </PrivateRoute>
          <PrivateRoute
            path={`/:shortName/${EVENTS}/:eventId/${PRODUCTS}/${PRODUCT_SUMMARY}`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <ProductSummary types={types} />
          </PrivateRoute>
          <PrivateRoute
            path={`/:shortName/${EVENTS}/:eventId/${PURCHASE}`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <OrderConfirmation types={types} />
          </PrivateRoute>
          <Route path={`/:shortName/${EVENTS}/:eventId/${PRODUCTS}`}>
            <EventProducts types={types} />
          </Route>
          <Route path={`/:shortName/${EVENTS}/:tabName`}>
            <Events types={types} />
          </Route>
          {hasNetwork && (
            <Route path={`/:shortName/${NETWORK}`}>
              <Network tabName={NETWORK} types={types} />
            </Route>
          )}
          <Route path={`/:shortName/${CREATE_ACCOUNT}`}>
            <CreateAccount types={types} />
          </Route>
          <PrivateRoute
            path={`/:shortName/${ACCOUNT}/${EDIT_PERSONAL_INFO}`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <EditPersonalInfo types={types} />
          </PrivateRoute>
          <Route path={`/:shortName/${LOGIN}`}>
            <Login types={types} />
          </Route>
          <PrivateRoute
            path={`/:shortName/${ACCOUNT}/${ACCOUNT_INFORMATION}`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <ViewAccount types={types} />
          </PrivateRoute>
          <PrivateRoute
            path={`/:shortName/${ACCOUNT}/${ORDER_HISTORY}`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <OrderHistory types={types} />
          </PrivateRoute>
          <PrivateRoute
            path={`/:shortName/${ACCOUNT}/${ORDER_DETAILS}/:orderId`}
            canGetUser={canGetUser}
            isLoading={isLoading}
          >
            <OrderDetailsContainer types={types} />
          </PrivateRoute>
          <Route path={`/:shortName/${FORGOT_PASSWORD}`}>
            <RecoverAccountPassword types={types} />
          </Route>
          <Route path={`/:shortName/${INFO}`}>
            <InfoMessage types={types} />
          </Route>
          <Route>
            <Redirect
              to={`/${shortName}/${EVENTS}/${SCHEDULED}${location.search}`}
            />
          </Route>
        </Switch>
      </Loading>
    );
  }
}

Home.propTypes = propTypes;
Home.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  eventTypes: state.events?.events?.types,
  client: state.client.client,
  branding: state.client.clientBranding,
  isFetching: state.client.isFetching,
  types: state.events.events.types,
  filters: state.filters.filters,
  user: state.user.user,
  isLoading: state.user.isLoading
});

const mapDispatchToProps = {
  fetchClientAction: fetchClient,
  fetchEventTypesAction: fetchEventTypes,
  updateFiltersAction: updateFilters,
  getAccountAction: getAccountByAccountId
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(Home);
